'use strict';

import { updateUIFromShadowRoot } from '@core/i18n/window-language';
import {
    SEARCH_IMG_RESULT_EVENT,
    SEARCH_IMG_REPORTS_RESULT_EVENT,
    UPDATE_LANGUAGE_EVENT,
} from '@core/constants/EVENTS';
import {
    globalSharedStyles,
    commonTabsLayoutStyles,
    typographySharedStyles,
} from '@core/styles';
import { CURRENT_TAB } from '@core/constants/CONSTANTS';
import { isReportDetailsView, isReportListView } from '@core/utils/report';
import { getCurrentSearcherObj, isAIActivated } from '@core/utils/searcher';
import { STORE_SEARCHER_FILTERS } from '@core/constants/STORE';
import { SEARCHER_TYPES } from '@core/constants/SEARCHER';
import { updateStoreFiltersState } from '@core/utils/store';

import htmlContent from './TabButtonsComponent.html';
import cssContent from './TabButtonsComponent.css';
import { TOGGLE_TAB_PANES_EVENT } from '../../CONSTANTS';
import {
    formatNumberForDisplay,
    updateTabButtonStates,
    updateTotalResultsDisplay,
    updateStoreWithResults,
    updateSearchOptionTranslation,
} from './utils';

/**
 * TabButtonsComponent - Manages tab navigation between images and reports views
 * with dynamic result counts and state management.
 *
 * @class TabButtonsComponent
 * @extends {HTMLElement}
 */
class TabButtonsComponent extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });

        /** @type {HTMLTemplateElement} */
        const template = document.createElement('template');
        template.innerHTML = htmlContent;

        const styleSheet = new CSSStyleSheet();
        styleSheet.replaceSync(cssContent);

        this.shadowRoot.adoptedStyleSheets = [
            globalSharedStyles,
            commonTabsLayoutStyles,
            typographySharedStyles,
            styleSheet,
        ];

        this.shadowRoot.appendChild(template.content.cloneNode(true));

        // Initialize properties
        this._handleTranslationHelperBound = this._handleTranslationHelper.bind(this);
        this._handleSearchImgResultBound = this._handleSearchImgResult.bind(this);
        this._handleSearchImgReportsResultBound =
            this._handleSearchImgReportsResult.bind(this);
        this._handleImagesTabClickBound = this._handleImagesTabClick.bind(this);
        this._handleReportsTabClickBound = this._handleReportsTabClick.bind(this);

        // State properties
        this._clickOnTab = false;
        this._currentSelectedTab = CURRENT_TAB.IMAGES;
        this._timeoutRefs = [];
    }

    /**
     * Called when the element is added to the DOM
     * @returns {void}
     */
    connectedCallback() {
        if (isReportListView() || isReportDetailsView()) {
            this.style.display = 'none';
            return;
        }

        if (isAIActivated()) {
            this._hideReportTab();
        }

        this._cacheDOMElements();
        this._addEventListeners();
    }

    /**
     * Called when the element is removed from the DOM
     * @returns {void}
     */
    disconnectedCallback() {
        this._removeEventListeners();
        this._clearTimeouts();
    }

    /**
     * Cache DOM elements for better performance
     * @returns {void}
     * @private
     */
    _cacheDOMElements() {
        this._imagesTabButtons = this.shadowRoot.querySelectorAll('[data-images-tab]');
        this._reportsTabButtons = this.shadowRoot.querySelectorAll('[data-reports-tab]');
        this._imagesButtons = this.shadowRoot.querySelectorAll('[data-images-button]');
        this._reportButtons = this.shadowRoot.querySelectorAll('[data-report-button]');
        this._totalImagesElements =
            this.shadowRoot.querySelectorAll('[data-total-images]');
        this._totalReportsElements =
            this.shadowRoot.querySelectorAll('[data-total-reports]');
    }

    /**
     * Add event listeners to the component
     * @returns {void}
     * @private
     */
    _addEventListeners() {
        document.addEventListener(
            UPDATE_LANGUAGE_EVENT,
            this._handleTranslationHelperBound,
        );

        document.addEventListener(
            SEARCH_IMG_RESULT_EVENT,
            this._handleSearchImgResultBound,
        );

        document.addEventListener(
            SEARCH_IMG_REPORTS_RESULT_EVENT,
            this._handleSearchImgReportsResultBound,
        );

        this._imagesButtons.forEach((button) => {
            button.addEventListener('click', this._handleImagesTabClickBound);
        });

        this._reportButtons.forEach((button) => {
            button.addEventListener('click', this._handleReportsTabClickBound);
        });
    }

    /**
     * Remove event listeners to prevent memory leaks
     * @returns {void}
     * @private
     */
    _removeEventListeners() {
        document.removeEventListener(
            UPDATE_LANGUAGE_EVENT,
            this._handleTranslationHelperBound,
        );

        document.removeEventListener(
            SEARCH_IMG_RESULT_EVENT,
            this._handleSearchImgResultBound,
        );

        document.removeEventListener(
            SEARCH_IMG_REPORTS_RESULT_EVENT,
            this._handleSearchImgReportsResultBound,
        );

        this._imagesButtons?.forEach((button) => {
            button.removeEventListener('click', this._handleImagesTabClickBound);
        });

        this._reportButtons?.forEach((button) => {
            button.removeEventListener('click', this._handleReportsTabClickBound);
        });
    }

    /**
     * Clear all timeout references to prevent memory leaks
     * @returns {void}
     * @private
     */
    _clearTimeouts() {
        this._timeoutRefs.forEach((timeoutRef) => {
            if (timeoutRef) {
                clearTimeout(timeoutRef);
            }
        });
        this._timeoutRefs = [];
    }

    /**
     * Translate the component elements
     * @returns {void}
     * @private
     */
    _handleTranslationHelper() {
        updateUIFromShadowRoot(this.shadowRoot);
    }

    /**
     * Handle search image results event
     * @param {CustomEvent} event - The search image results event
     * @returns {void}
     * @private
     */
    _handleSearchImgResult(event) {
        if (event.detail.page === 0) {
            this._fillTotalResults(event.detail);
            this._commonCodeClickTabImages(false);
        } else {
            this._getFillTotalResultsFromStore();
        }

        this._handleTranslationHelper();
        this._commonCodeHandleReportTab();
    }

    /**
     * Handle search image reports results event
     * @param {CustomEvent} event - The search image reports results event
     * @returns {void}
     * @private
     */
    _handleSearchImgReportsResult(event) {
        if (event.detail.page === 0) {
            this._fillTotalResults(event.detail);
            this._commonCodeClickTabReports(false);
        } else {
            this._getFillTotalResultsFromStore();
        }

        this._handleTranslationHelper();
        this._commonCodeHandleReportTab();
    }

    /**
     * Handle images tab click
     * @returns {void}
     * @private
     */
    _handleImagesTabClick() {
        this._clickOnTab = true;
        this._commonCodeClickTabImages();
    }

    /**
     * Handle reports tab click
     * @returns {void}
     * @private
     */
    _handleReportsTabClick() {
        this._clickOnTab = true;
        this._commonCodeClickTabReports();
    }

    /**
     * Common code to handle the images tab
     * @param {boolean} [dispatchEvent=true] - If the event should be dispatched
     * @returns {void}
     * @private
     */
    _commonCodeClickTabImages(dispatchEvent = true) {
        this._showTabs(CURRENT_TAB.IMAGES, dispatchEvent);
        updateTabButtonStates(this._imagesTabButtons, this._reportsTabButtons, true);
    }

    /**
     * Common code to handle the reports tab
     * @param {boolean} [dispatchEvent=true] - If the event should be dispatched
     * @returns {void}
     * @private
     */
    _commonCodeClickTabReports(dispatchEvent = true) {
        this._showTabs(CURRENT_TAB.REPORTS, dispatchEvent);
        updateTabButtonStates(this._imagesTabButtons, this._reportsTabButtons, false);
    }

    /**
     * Fill the total results with formatted numbers
     * @param {Object} props - Total images and reports
     * @param {number|string} props.totalImgs - The total images
     * @param {number|string} props.totalReports - The total reports
     * @returns {void}
     * @private
     */
    _fillTotalResults({ totalImgs, totalReports }) {
        const formattedTotalImgs = formatNumberForDisplay(totalImgs);
        const formattedTotalReports = formatNumberForDisplay(totalReports);

        updateTotalResultsDisplay(
            this._totalImagesElements,
            formattedTotalImgs,
            this._clickOnTab,
        );

        updateTotalResultsDisplay(
            this._totalReportsElements,
            formattedTotalReports,
            this._clickOnTab,
        );

        updateStoreWithResults(
            formattedTotalImgs,
            formattedTotalReports,
            this._clickOnTab,
        );

        updateSearchOptionTranslation(this._imagesButtons);
        this._handleTranslationHelper();

        this._clickOnTab = false;
    }

    /**
     * Fill total results from the store
     * @returns {void}
     * @private
     */
    _getFillTotalResultsFromStore() {
        const currentFiltersStateHistory = getCurrentSearcherObj();

        if (!currentFiltersStateHistory) return;

        this._totalImagesElements.forEach((totalImg) => {
            totalImg.innerHTML =
                currentFiltersStateHistory[STORE_SEARCHER_FILTERS.TOTAL_IMGS];
        });

        this._totalReportsElements.forEach((totalReport) => {
            totalReport.innerHTML =
                currentFiltersStateHistory[STORE_SEARCHER_FILTERS.TOTAL_REPORTS];
        });
    }

    /**
     * Show tabs and dispatch event
     * @param {string} tabName - The tab name to show
     * @param {boolean} [dispatchEvent=true] - If the event should be dispatched
     * @returns {void}
     * @private
     */
    _showTabs(tabName, dispatchEvent = true) {
        this._currentSelectedTab = tabName;

        if (dispatchEvent) {
            document.dispatchEvent(
                new CustomEvent(TOGGLE_TAB_PANES_EVENT, {
                    detail: {
                        tabName,
                        dispatchEvent,
                    },
                }),
            );
        }

        this._updateCurrentSelectedTabFiltersState();
    }

    /**
     * Hide the report tab
     * @returns {void}
     * @private
     */
    _hideReportTab() {
        if (this._currentSelectedTab === CURRENT_TAB.REPORTS) {
            this._showTabs(CURRENT_TAB.IMAGES);
            updateTabButtonStates(this._imagesTabButtons, this._reportsTabButtons, true);
        }

        this._reportButtons?.forEach((tabButton) => {
            if (tabButton.hasAttribute('data-images-tab')) {
                tabButton.classList.remove('active-tab');
            }
        });
    }

    /**
     * Common code to handle the report tab
     * @returns {void}
     * @private
     */
    _commonCodeHandleReportTab() {
        if (this.reportDetailsView || this.reportsView) {
            return;
        }

        if (isAIActivated()) {
            this._hideReportTab();
        } else {
            this._showReportTab();
        }
    }

    /**
     * Show the report tab
     * @returns {void}
     * @private
     */
    _showReportTab() {
        if (this._currentSelectedTab === CURRENT_TAB.IMAGES) {
            this._reportButtons?.forEach((tabButton) => {
                if (tabButton.hasAttribute('data-images-tab')) {
                    tabButton.classList.add('active-tab');
                }
            });
        }
    }

    /**
     * Update the current selected tab filters state
     * @returns {void}
     * @private
     */
    _updateCurrentSelectedTabFiltersState() {
        const currentFiltersStateHistory = getCurrentSearcherObj();
        const urlParams = new URLSearchParams(window.location.search);

        if (!currentFiltersStateHistory) return;

        currentFiltersStateHistory[STORE_SEARCHER_FILTERS.SELECTED_TAB] =
            this._currentSelectedTab;
        updateStoreFiltersState(
            urlParams.get(SEARCHER_TYPES.SEARCHER_ID),
            currentFiltersStateHistory,
        );
    }
}

customElements.define('tab-buttons-component', TabButtonsComponent);
